import React, { useState } from "react";
import { Input, Button } from "@swordsweeper/ssi-ui-component-library";
import { useFormHandlers } from "@swordsweeper/react-forms";
import AvatarSelect from "./AvatarSelect";
import isEqual from "lodash/isEqual";
import get from "lodash/get";
import * as Yup from "yup";

interface AccountGeneralSettingsData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  avatarBackgroundColor: string;
  avatarImage: File | string | null;
  initials: string;
}

const emailRegex = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const phoneRegex =
  /^\(?([0-9]{3})\)?\s*[-.]?\s*([0-9]{3})\s*[-.]?\s*([0-9]{4})$/;

export const schema = Yup.object().shape({
  firstName: Yup.string().required("First Name is a required field"),
  lastName: Yup.string().required("Last Name is a required field"),
  email: Yup.string()
    .required("Email is a required field")
    .test(
      "email-format",
      "Must be a valid email address.",
      (value) => !value || emailRegex.test(value),
    ),
  phone: Yup.string().test(
    "phone-optional",
    "Phone number must be exactly 10 digits long.",
    (value) => !value || phoneRegex.test(value),
  ),
});

export interface UserGeneralSettingsFormErrors {
  firstName?: string[];
  lastName?: string[];
  email?: string[];
  phone?: string[];
}

interface AccountGeneralSettingsFormProps {
  initialData: AccountGeneralSettingsData;
  onSubmit: (formData: AccountGeneralSettingsData) => void;
  formErrors?: UserGeneralSettingsFormErrors;
}

type ValidatedField = keyof UserGeneralSettingsFormErrors;

const collapseSpaces = (value: string): string => value.replace(/\s{2,}/g, " ");

const formatPhone = (value: string): string => {
  const normalizedValue = value || "";
  const digitsOnly = normalizedValue.replace(/\D/g, "").slice(0, 10);
  if (!digitsOnly) return "";
  const area = digitsOnly.slice(0, 3);
  const prefix = digitsOnly.slice(3, 6);
  const line = digitsOnly.slice(6);
  if (digitsOnly.length <= 3) return digitsOnly;
  if (digitsOnly.length <= 6) return `(${area}) ${prefix}`;
  return `(${area}) ${prefix}-${line}`;
};

export default function AccountGeneralSettingsForm({
  initialData,
  onSubmit,
  formErrors,
}: AccountGeneralSettingsFormProps) {
  const normalizedInitialData = {
    ...initialData,
    phone: initialData.phone ?? "",
  };
  const { formData, handleUpdate, isValid } =
    useFormHandlers<AccountGeneralSettingsData>(normalizedInitialData, schema);
  const [clientErrors, setClientErrors] = useState<
    Partial<Record<ValidatedField, string[]>>
  >({});
  const [userAvatarImageSrc, setUserAvatarImageSrc] = useState<string | null>(
    () => {
      if (initialData.avatarImage instanceof File) {
        return URL.createObjectURL(initialData.avatarImage);
      } else if (typeof initialData.avatarImage === "string") {
        return initialData.avatarImage;
      }
      return null;
    },
  );
  const hasChanges = !isEqual(normalizedInitialData, formData);

  const validateField = async (field: ValidatedField): Promise<void> => {
    try {
      await schema.validateAt(field, formData);
      setClientErrors((prev) => ({ ...prev, [field]: [] }));
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        setClientErrors((prev) => ({ ...prev, [field]: [err.message] }));
      }
    }
  };

  const handleAvatarImageChanged = (file: File | null): void => {
    handleUpdate({ name: "avatarImage", value: file });
    setUserAvatarImageSrc(!!file ? URL.createObjectURL(file) : null);
  };

  const handlePhoneChange = (value: string): void => {
    const normalizedValue = value || "";
    const digitsOnly = normalizedValue.replace(/\D/g, "").slice(0, 10);
    handleUpdate({ name: "phone", value: digitsOnly });
  };

  const handlePhoneBlur = (): void => {
    const normalizedPhone = formData.phone || "";
    if (!formData.phone)
      handleUpdate({ name: "phone", value: normalizedPhone });
    validateField("phone");
  };

  const firstNameFormErrors = get(formErrors, "firstName", []);
  const firstNameClientErrors = get(clientErrors, "firstName", []);
  const firstNameErrors = [...firstNameFormErrors, ...firstNameClientErrors];
  const hasFirstNameError = firstNameErrors.length > 0;

  const lastNameFormErrors = get(formErrors, "lastName", []);
  const lastNameClientErrors = get(clientErrors, "lastName", []);
  const lastNameErrors = [...lastNameFormErrors, ...lastNameClientErrors];
  const hasLastNameError = lastNameErrors.length > 0;

  const emailFormErrors = get(formErrors, "email", []);
  const emailClientErrors = get(clientErrors, "email", []);
  const emailErrors = [...emailFormErrors, ...emailClientErrors];
  const hasEmailError = emailErrors.length > 0;

  const phoneFormErrors = get(formErrors, "phone", []);
  const phoneClientErrors = get(clientErrors, "phone", []);
  const phoneErrors = [...phoneFormErrors, ...phoneClientErrors];
  const hasPhoneError = phoneErrors.length > 0;
  const formattedPhone = formatPhone(formData.phone);
  return (
    <div className="flex-column spaced-content-v-xl">
      <div className="flex-row spaced-content-xl">
        <div>
          <div className="text-lg font-weight-bold padding-y-lg">
            Contact Information
          </div>
          <div className="flex-column spaced-content-v-xl">
            <div className="flex-row spaced-content">
              <Input
                defaultValue=""
                onChange={(value: string) =>
                  handleUpdate({
                    name: "firstName",
                    value: collapseSpaces(value),
                  })
                }
                onBlur={() => validateField("firstName")}
                value={formData.firstName}
                width="50%"
                label="First Name"
                error={hasFirstNameError}
              />
              <Input
                defaultValue=""
                onChange={(value: string) =>
                  handleUpdate({
                    name: "lastName",
                    value: collapseSpaces(value),
                  })
                }
                onBlur={() => validateField("lastName")}
                value={formData.lastName}
                width="50%"
                label="Last Name"
                error={hasLastNameError}
              />
            </div>

            {(hasFirstNameError || hasLastNameError) && (
              <div className="text-xs text-error font-weight-bold">
                {firstNameErrors.map((e) => (
                  <div>{e}</div>
                ))}
                {lastNameErrors.map((e) => (
                  <div>{e}</div>
                ))}
              </div>
            )}

            <Input
              defaultValue=""
              onChange={(value: string) =>
                handleUpdate({ name: "email", value: collapseSpaces(value) })
              }
              onBlur={() => validateField("email")}
              value={formData.email}
              width="100%"
              label="Email"
              type="email"
              error={hasEmailError}
            />

            {hasEmailError && (
              <div className="text-xs text-error font-weight-bold">
                {emailErrors.map((e) => (
                  <div>{e}</div>
                ))}
              </div>
            )}

            <Input
              defaultValue=""
              onChange={handlePhoneChange}
              onBlur={handlePhoneBlur}
              value={formattedPhone ?? ""}
              width="100%"
              label="Phone"
              type="tel"
              inputMode="numeric"
              onRenderValue={formatPhone}
              error={hasPhoneError}
            />

            {hasPhoneError && (
              <div className="text-xs text-error font-weight-bold">
                {phoneErrors.map((e) => (
                  <div>{e}</div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div>
          <div className="text-lg font-weight-bold padding-y-lg">Avatar</div>
          <AvatarSelect
            userInitials={initialData.initials}
            backgroundColor={formData.avatarBackgroundColor}
            userAvatarImageSrc={userAvatarImageSrc}
            onChangeBackgroundColor={(value: string) =>
              handleUpdate({ name: "avatarBackgroundColor", value })
            }
            onChangeAvatarImage={handleAvatarImageChanged}
          />
        </div>
      </div>

      <div>
        <Button
          className="btn btn-primary btn-md"
          label="Save Changes"
          onClick={() => onSubmit(formData)}
          disabled={!isValid || !hasChanges}
          data-testid="account-general-settings-save-button"
        />
      </div>
    </div>
  );
}
